import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  ReactNode,
  forwardRef,
  useState
} from 'react';
import {
  ControllerRenderProps,
  FieldValues,
  Path,
  UseControllerProps,
  useController
} from 'react-hook-form';
import { Input } from '../../atoms';
import { FormField } from './form-field';

type FieldDurationInputProps<TFieldValues extends FieldValues> = Omit<
  ComponentPropsWithoutRef<'input'>,
  'size'
> &
  UseControllerProps<TFieldValues> & {
    name: Path<TFieldValues>;
    label: string;
    sublabel?: string;
    defaultValue?: number;
  };

export function FieldDurationInput<
  TFieldValues extends FieldValues = FieldValues
>({
  id,
  name,
  label,
  sublabel,
  min,
  max,
  ...controllerProps
}: FieldDurationInputProps<TFieldValues>): ReactNode {
  const {
    field: { value: defaultValue, ...field },
    fieldState
  } = useController({ name, ...controllerProps });

  return (
    <FormField
      id={id ?? name}
      name={name}
      label={label}
      sublabel={sublabel}
      error={fieldState.error}
      isRequired={!!controllerProps.rules?.required}
    >
      <DurationInput
        {...field}
        id={id ?? name}
        defaultValue={defaultValue}
        required={!controllerProps.rules?.required}
        min={min}
        max={max}
      />
    </FormField>
  );
}

type DurationInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'size'> &
  Omit<ControllerRenderProps, 'value'> & { defaultValue?: number };

const DurationInput = forwardRef<HTMLInputElement, DurationInputProps>(
  ({ defaultValue = 0, ...props }, ref) => {
    const [value, setValue] = useState<string>(
      toDurationInputValue(defaultValue)
    );

    function getDuration(
      e: ChangeEvent<HTMLInputElement>
    ): void | undefined | number {
      const inputValue = e.target.value;
      if (inputValue === '') {
        setValue('');
        return undefined;
      }

      const regMatch = inputValue.match(
        /^(\d{1,2})(:{0,1})([0-5][0-9]{0,1}){0,1}$/
      );

      if (regMatch) {
        const inputHours = regMatch[1];
        const inputMinutes = regMatch[3];
        setValue(`${inputHours}${inputMinutes ? `:${inputMinutes}` : ''}`);

        const hoursInMinutes = Number(regMatch[1]) * 60;
        const minutes = Number(regMatch[3] ?? 0);

        return hoursInMinutes + minutes;
      }
    }

    return (
      <Input
        type="text"
        {...props}
        ref={ref}
        value={value}
        onChange={(e) => {
          const duration = getDuration(e);
          if (typeof duration === 'number' || typeof duration === 'undefined') {
            props.onChange(duration);
          }
        }}
      />
    );
  }
);

export function toDurationInputValue(defaultValue: number): string {
  const hours = Math.floor(defaultValue / 60);
  const minutes = defaultValue % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}`;
}
